import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { InsurancePolicy, PremiumPayment } from '@/types/insurance';

export interface CreatePremiumPaymentData {
  policy_id: string;
  amount: number;
  currency: string;
  payment_date: string;
  notes?: string | null;
}

export interface ValidationError {
  field: string;
  message: string;
}

export function validatePremiumPayment(
  data: CreatePremiumPaymentData,
  policy: InsurancePolicy
): ValidationError | null {
  if (data.amount <= 0) {
    return { field: 'amount', message: 'Amount must be greater than 0' };
  }
  if (data.payment_date > policy.end_date) {
    return { field: 'payment_date', message: 'Payment date cannot be after the policy end date' };
  }
  return null;
}

export function useCreatePremiumPayment() {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      data,
      policy,
    }: {
      data: CreatePremiumPaymentData;
      policy: InsurancePolicy;
    }) => {
      const validationError = validatePremiumPayment(data, policy);
      if (validationError) {
        throw new Error(validationError.message);
      }

      const { data: inserted, error } = await supabase
        .from('insurance_premium_payments')
        .insert({ ...data, user_id: user!.id })
        .select()
        .single();

      if (error) throw error;
      return inserted as PremiumPayment;
    },
    onSuccess: (_result, { data }) => {
      qc.invalidateQueries({ queryKey: ['policy-payments', data.policy_id] });
    },
  });
}
